import React from 'react';
import styled from 'styled-components';
import { Header } from '@app/navigation/header';
import { Body } from '@ui';
import { useNavigate } from 'react-router';
import { ROUTES } from '@constants/routes';
import { logNetRequest } from '@utils/usefulFuncs';
import { Body1, Body5, Container, Page } from '@ui';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FlatButton } from '@ui/buttons';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { createTimeInstance } from '@utils/time';
import { Working } from '@ui/Working';
import ErrorText from '@ui/ErrorText';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const FormWrapper = styled.div`
  padding: 24px 0 0 0;

  select {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    color: ${({ theme }) => theme.themeColors.text};
    background-color: transparent;
    border-radius: 6px;
    width: 100%;
    max-width: 420px;
    padding: 12px 16px;
    -webkit-appearance: auto !important;
  }
`;
const DatePickerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-start;

  select {
    width: 120px;
  }
`;

const ResultsWrapper = styled.div`
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const FileWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-start;
  width: 240px;

  padding: 12px;

  height: 40px;
  border: 1px solid ${({ theme }) => theme.themeColors.text};
  border-radius: 5px;
  cursor: pointer;
  transition: all 140ms;

  &:hover {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton};
    * {
      color: ${({ theme }) => theme.themeColors.buttonText};
      fill: ${({ theme }) => theme.themeColors.buttonText};
    }
  }

  svg {
    height: 16px;
    width: 16px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const availableDocuments = ['Statements', 'Confirms', 'Tax'];

const validateForm = (form) => {
  if (!form.document_type) {
    return 'Please select a document type';
  }
  if (!form.start_date) {
    return 'Please select a start date';
  }
  if (!form.end_date) {
    return 'Please select an end date';
  }
  const start = createTimeInstance(form.start_date_full);
  const end = createTimeInstance(form.end_date_full);
  if (start.isAfter(end)) {
    return 'Start date must be before end date';
  }
  return null;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const YEARS = ['2018', '2019', '2020', '2021', '2022', '2023', '2024', '2025', '2026'];

const buildAPIDate = (month, year) => {
  const date = createTimeInstance(`${month}-01-${year}`);
  return date.format('MM[-]YYYY');
};

function ResultItem({ data }) {
  const { url, date } = data;
  const displayDate = createTimeInstance(date).format('MMM YYYY');
  const handleClick = () => {
    window.open(url, '_blank');
  };
  return (
    <FileWrapper onClick={handleClick}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
      </svg>
      <Body5 bold>{displayDate}</Body5>
    </FileWrapper>
  );
}

function Results({ results }) {
  if (results.length === 0) {
    return (
      <Container bottom={24} top={24}>
        <Body5 bold>No results found</Body5>
      </Container>
    );
  }
  return (
    <Container top={8}>
      <Body1 bold>Results</Body1>
      <ResultsWrapper>
        {results.map((result, index) => (
          <ResultItem key={index} data={result} />
        ))}
      </ResultsWrapper>
    </Container>
  );
}

function BrokerageTaxDocuments() {
  const navigate = useNavigate();

  const [results, setResults] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [isSearching, setIsSearching] = React.useState(false);

  const [documentType, setDocumentType] = React.useState('Tax');
  const [startDateMonth, setStartDateMonth] = React.useState('Jan');
  const [startDateYear, setStartDateYear] = React.useState('2025');
  const [endDateMonth, setEndDateMonth] = React.useState('Jan');
  const [endDateYear, setEndDateYear] = React.useState('2026');

  const onDocumentTypeChange = (e) => {
    setDocumentType(e.target.value);
  };

  const onStartDateMonthChange = (e) => {
    setStartDateMonth(e.target.value);
  };

  const onStartDateYearChange = (e) => {
    setStartDateYear(e.target.value);
  };

  const onEndDateMonthChange = (e) => {
    setEndDateMonth(e.target.value);
  };

  const onEndDateYearChange = (e) => {
    setEndDateYear(e.target.value);
  };

  const onGoBack = () => {
    navigate(ROUTES.home);
  };

  const onSearchDocumentsClick = async () => {
    const form = {
      document_type: documentType,
      start_date: buildAPIDate(startDateMonth, startDateYear),
      start_date_full: `${startDateMonth}-01-${startDateYear}`,
      end_date: buildAPIDate(endDateMonth, endDateYear),
      end_date_full: `${endDateMonth}-01-${endDateYear}`,
    };
    const formValidationError = validateForm(form);
    if (formValidationError) {
      setError(formValidationError);
      return;
    }
    setResults(null);
    setError(null);
    setIsSearching(true);
    const URL = `documents?document_type=${form.document_type.toLowerCase()}&start_date=${form.start_date}&end_date=${
      form.end_date
    }`;
    const { status, data, error } = await sendApiRequest('get', URL, form);
    logNetRequest(status, data, error);
    handle400Statuses(status);
    setIsSearching(false);
    if (status === 200 && !data?.error) {
      setResults(data.documents);
    } else {
      setError('Something went wrong, please try again.');
    }
  };

  React.useEffect(() => {
    if (results !== null) {
      const element = document.getElementById('brokerage-documents-results');
      if (element) {
        const yOffset = -26;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        setTimeout(() => {
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 300);
      }
    }
  }, [results]);

  return (
    <PageWrapper>
      <Header noActions />
      <Body withFooter>
        <Page>
          <BackArrowButton onClick={onGoBack} />
          <Container top={40}>
            <Body1 bold>Documents</Body1>

            <FormWrapper>
              <Container>
                <Container>
                  <Body5>Document Type</Body5>
                </Container>
                <Container top={8}>
                  <select value={documentType} onChange={onDocumentTypeChange}>
                    {availableDocuments.map((document) => (
                      <option key={document} value={document}>
                        {document}
                      </option>
                    ))}
                  </select>
                </Container>
              </Container>
              <Container top={16}>
                <Container>
                  <Body5>Start Date</Body5>
                </Container>
                <Container top={8}>
                  <DatePickerWrapper>
                    <select value={startDateMonth} onChange={onStartDateMonthChange}>
                      {MONTHS.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <select value={startDateYear} onChange={onStartDateYearChange}>
                      {YEARS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </DatePickerWrapper>
                </Container>
              </Container>
              <Container top={16}>
                <Container>
                  <Body5>End Date</Body5>
                </Container>
                <Container top={8}>
                  <DatePickerWrapper>
                    <select value={endDateMonth} onChange={onEndDateMonthChange}>
                      {MONTHS.map((month) => (
                        <option key={month} value={month}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <select value={endDateYear} onChange={onEndDateYearChange}>
                      {YEARS.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </DatePickerWrapper>
                </Container>
              </Container>
              <Container top={24}>
                {error && (
                  <Container bottom={16}>
                    <ErrorText>{error}</ErrorText>
                  </Container>
                )}
                <FlatButton fullWidth onClick={onSearchDocumentsClick}>
                  Search Documents
                </FlatButton>
              </Container>
            </FormWrapper>
          </Container>
        </Page>
        {isSearching && (
          <Page>
            <Container top={80} bottom={80}>
              <Working noVerticalPadding />
            </Container>
          </Page>
        )}
        <a id="#brokerage-documents-results" />
        {results !== null && (
          <Page>
            <Results results={results} />
          </Page>
        )}
      </Body>
    </PageWrapper>
  );
}

export default BrokerageTaxDocuments;
